import { skipToken } from "@reduxjs/toolkit/query";

import { useAppSelector } from "~/app/store";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import { isFetchBaseQueryError } from "~/lib/isFetchBaseQueryError";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetWorkstationInductionConfigurationQuery } from "~/redux/warehouse/workstation.hooks";
import { TemperatureZone } from "~/types/api";

/**
 * The workstation's saved induction temperature zone. `tempZone` is null when
 * never configured. `isLoading` is true while that GET is in flight, including
 * refetches after a POST invalidation.
 */
export function useInductionConfiguration(): {
  isLoading: boolean;
  error: unknown;
  tempZone: TemperatureZone | null;
} {
  const { isMultiTempGrid } = useGetMultiTempGridInfo();
  const siteWorkstation = useAppSelector(selectThisWorkstation);

  const shouldFetch = isMultiTempGrid && !!siteWorkstation;

  const { data, error, isUninitialized, isFetching } =
    useGetWorkstationInductionConfigurationQuery(
      shouldFetch
        ? {
            autostoreGridId: siteWorkstation.autostoreGridId,
            workstationId: siteWorkstation.id
          }
        : skipToken,
      { refetchOnMountOrArgChange: true }
    );

  const isNotConfigured =
    isFetchBaseQueryError(error) &&
    (error.status === 404 ||
      ("originalStatus" in error && error.originalStatus === 404));

  return {
    isLoading: shouldFetch && (isFetching || isUninitialized),
    error: isNotConfigured ? undefined : error,
    tempZone: data?.configuration.temperatureZone || null
  };
}
